// Пользователь, сеанс, ссылка-приглашение (contracts/api.md, «Пользователи»; data-model.md, access.*).
import { z } from '../kernel/zod.ts';
import { AuditEntrySchema } from '../audit/entry.ts';
import { SessionTermSchema, SessionViaSchema } from '../auth/link.ts';
import { IdSchema, TimestampSchema, VersionSchema } from '../kernel/common.ts';
import { fieldMessages } from '../kernel/field-messages.ts';
import { RoleSchema, UserKindSchema } from '../rights/roles.ts';
import { AssignmentSchema } from './assignment.ts';
import { ExternalStateSchema } from './email-policy.ts';

/** Состояние вычисляется: приглашён → активен ↔ заблокирован; любое ↔ в архиве. */
export const userStates = ['invited', 'active', 'locked', 'archived'] as const;
export const UserStateSchema = z.enum(userStates);
export type UserState = (typeof userStates)[number];

export const UserSchema = z.object({
  id: IdSchema,
  email: z.string(),
  /** Фамилия первой: «Морозов Олег Сергеевич» или «Морозов О. С.». */
  fullName: z.string(),
  state: UserStateSchema,
  lockedUntil: TimestampSchema.optional(),
  version: VersionSchema,
  createdAt: TimestampSchema,
  archivedAt: TimestampSchema.optional(),
  /**
   * 022 (research, Р2): внешний пользователь — «Внешний» (active) или «Внешний доступ выключен»
   * (disabled); null или нет поля — сотрудник компании. Считается по текущей настройке.
   */
  external: ExternalStateSchema.nullable().optional(),
});
export type User = z.infer<typeof UserSchema>;

/**
 * Строка списка пользователей: действующие роли и вид (040, FR-018) — для колонки «Роль»:
 * «Администратор», «Сотрудник», «Внешний», «Сопровождение».
 */
export const UserListItemSchema = UserSchema.extend({
  roles: z.array(RoleSchema),
  kind: UserKindSchema,
});
export type UserListItem = z.infer<typeof UserListItemSchema>;

/** Сеанс в карточке пользователя: когда начат, последнее действие, устройство (FR-009б). */
export const SessionSchema = z.object({
  id: IdSchema,
  createdAt: TimestampSchema,
  lastSeenAt: TimestampSchema,
  /** Из User-Agent; адреса нет (ADR-0022). */
  device: z.string().optional(),
  /** 039 (FR-023, FR-024): способ входа — пароль, ссылка из письма, код из письма. */
  via: SessionViaSchema,
  /** 039: когда сеанс закончится без действий — по сроку класса (data-model.md). */
  until: TimestampSchema,
});
export type Session = z.infer<typeof SessionSchema>;

/** 039 (FR-023): свой сеанс в «Сеансах» — с отметкой «Этот». */
export const OwnSessionSchema = SessionSchema.extend({ current: z.boolean() });
export type OwnSession = z.infer<typeof OwnSessionSchema>;

/** 039: GET /api/auth/sessions — срок сеанса по ролям сейчас и свои действующие сеансы. */
export const OwnSessionsSchema = z.object({
  term: SessionTermSchema,
  sessions: z.array(OwnSessionSchema),
});
export type OwnSessions = z.infer<typeof OwnSessionsSchema>;

export const invitePurposes = ['invite', 'reset'] as const;
export const InvitePurposeSchema = z.enum(invitePurposes);
export type InvitePurpose = (typeof invitePurposes)[number];

/** Выпущенная ссылка — показывается один раз (FR-003а). */
export const IssuedInviteSchema = z.object({
  url: z.string(),
  expiresAt: TimestampSchema,
});
export type IssuedInvite = z.infer<typeof IssuedInviteSchema>;

/** Действующая ссылка пользователя в карточке, без токена. */
export const ActiveInviteSchema = z.object({
  purpose: InvitePurposeSchema,
  expiresAt: TimestampSchema,
});

/** GET /api/users/{id}: из этих данных окно подтверждения считает последствия. */
export const UserDetailSchema = UserSchema.extend({
  assignments: z.array(AssignmentSchema),
  sessions: z.array(SessionSchema),
  /** 041 (FR-004): протоколы, которыми пользователь владеет, — по номеру. */
  ownedProtocols: z.array(
    z.object({ documentId: IdSchema, number: z.number().int().min(1), title: z.string() }),
  ),
  /** 041, ревью T046: протоколов во владении в корзине — их архив тоже передаёт. */
  trashedOwnedCount: z.number().int().min(0),
  /** Последние записи журнала по пользователю — 10 штук. */
  recentAudit: z.array(AuditEntrySchema),
  activeInvite: ActiveInviteSchema.optional(),
});
export type UserDetail = z.infer<typeof UserDetailSchema>;

/** Пользователь с назначениями ролей — ответ возврата из архива. */
export const UserWithAssignmentsSchema = UserSchema.extend({
  assignments: z.array(AssignmentSchema),
});
export type UserWithAssignments = z.infer<typeof UserWithAssignmentsSchema>;

/** ФИО во входящих данных: 2–200 знаков. */
export const FullNameInputSchema = z
  .string({ error: fieldMessages.fullName })
  .trim()
  .min(2, { error: fieldMessages.fullName })
  .max(200, { error: fieldMessages.fullName });

/** Почта для входа во входящих данных; домен из списка проверяет сервер (REC-004). */
export const EmailInputSchema = z
  .string({ error: fieldMessages.email })
  .trim()
  .max(254, { error: fieldMessages.email })
  .pipe(z.email({ error: fieldMessages.email }));
